import _ from 'lodash'
import moment from 'moment'
import { vals } from '../../util'
import api from '../../axios_api'
import Promise from 'bluebird'
// this store is mostly about calculating the information for various charts/graphs/etc

const MAX_SECTIONS = 21

let state = {
  genderData: {},
  category: '',
  subCategory: '',
  fromDate: '',
  toDate: '',
  bubbleFromDate: '',
  bubbleToDate: '',
  tractionData: [],
  tractionLastUpdatedDate: '',
  portfolioTractionData: [],
  fundKpiData: []
}

const getters = {
  companyComparisonByProperty: (state, getters, rootState, rootGetters) => (companyName, property) => {
    // get all companies (capped at 10, including the primary, ordered by value)
    if (_.isNil(companyName) || _.isNil(property)) return {}
    let retval = { value: [], cost: [], names: [] }
    let data = rootGetters['portfolio/portfolioData']
    if (_.isNil(data) || _.isEmpty(data)) return retval
    // get our company
    let company = _.reduce(data, (o, c) => {
      if (c.name == companyName) return c
      return o
    }, {})
    if (_.isNil(company) || _.isEmpty(company)) return retval
    // make sure company is first in return data
    retval.value.push(company.value)
    retval.cost.push(company.cost)
    retval.names.push(company.name)
    // go through the rest of the companies, if their property matches the primary then hold on to it
    let others = _.map(data, c => {
      if (c.name == companyName || c[property] != company[property]) return null
      return c
    })
    others = _.without(others, null, undefined)
    // now go through and order based on value
    others = _.orderBy(others, ['value'], ['desc'])
    // do we have more than 9 (10th is primary)?
    if (others.length > (MAX_SECTIONS - 1)) {
      // reduce all after 8 to 'Others' option
      let i = 0
        , other = { value: 0, cost: 0, name: 'Other' }
      _.each(others, item => {
        i++
        if (i > (MAX_SECTIONS - 2)) {
          other.value += item.value
          other.cost += item.cost
        }
      })
      others = _.slice(others, 0, (MAX_SECTIONS - 1))
      others.push(other)
    }
    // push onto retval and return
    _.each(others, item => {
      retval.value.push(item.value)
      retval.cost.push(item.cost)
      retval.names.push(item.name)
    })
    return retval
  },
  companyRelativeValueByProperty: (state, getters, rootState, rootGetters) => (companyName, property) => {
    if (_.isNil(property) || _.isArray(property) || _.isObject(property)) return {}
    if (_.isNil(companyName)) return {}
    if (!_.isString(property)) property = property.toString()
    let retval = { 'value': 0, 'value_p': 0, 'other': 0, 'totalValue': 0 }
    let data = rootGetters['portfolio/portfolioData']
    if (_.isNil(data) || _.isEmpty(data)) return retval;
    // get our company
    let company = _.reduce(data, (o, c) => {
      if (c.name === companyName) return c
      return o
    }, {})
    _.each(data, gram => {
      if (!_.isNil(gram[property]) && !_.isNil(gram.value) && !_.isNaN(gram.value)) {
        if (gram.name === companyName) {
          retval.value = gram.value
          retval.totalValue += gram.value
        } else if (gram[property] === company[property]) {
          retval.other += gram.value
          retval.totalValue += gram.value
        }
      }
    })
    // get the percentage
    retval.value_p = (retval.value / retval.totalValue).toFixed(2)
    return retval
  },
  // portfolioValueByTime: (state, getters, rootState, rootGetters) => () => {
  //   // need to get the portfolio value over time of the companies (initial_investment)
  //   let retval = {}
  // },
  portfolioValueByProperty: (state, getters, rootState, rootGetters) => (property) => {
    if (_.isNil(property) || _.isArray(property) || _.isObject(property)) return {} // invalid property type
    if (!_.isString(property)) property = property.toString()
    // create map of the passed in property and add up the value of each of the rows
    // we want the % of the total value of each sector
    let retval = { 'value': [], 'value_p': [] }
    retval[property] = []
    let totalValue = 0
    // make sure we have portfolioData
    let data = rootGetters['portfolio/portfolioData']
    if (_.isNil(data) || data.length < 1) return retval // nothing to calc
    let fprop = _.reduce(data, (obj, d) => {
      if (!_.isNil(d[property]) && !_.isNil(d.value) && !_.isNaN(d.value)) {
        // conert to title cast (start), keeping any hyphens
        let key = typeof d[property] === 'string' ? d[property].split('-') : [d[property]]
        key = _.join(_.map(key, k => { return k }), '-')
        obj[key] = (!_.isNil(obj[key])) ? obj[key] + d.value : d.value
        //totalValue += d.value
        totalValue = !isNaN(d.value) ? totalValue + Number(d.value) : totalValue + 0;
      }
      return obj
    }, {})
    // make object into array of objs {key,value}
    fprop = _.flatMap(fprop, (v, k) => {
      return { key: k, value: v }
    })
    // sort by value (desc)
    fprop = _.orderBy(fprop, ['value'], ['desc'])
    // if there are more than MAX_SECTIONS, combine the lowest into 'Other'
    if (fprop.length > MAX_SECTIONS) {
      let i = 0,
        other = { key: 'Other', value: 0 }
      _.each(fprop, item => {
        i++;
        if (i > (MAX_SECTIONS - 1)) {
          //other.value += item.value
          other.value = !isNaN(item.value) ? other.value + Number(item.value) : other.value + 0;
        }
      })
      fprop = _.slice(fprop, 0, MAX_SECTIONS)
      fprop.push(other)
    }
    // go through our accumulations and assign them to the arrays
    _.each(fprop, item => {
      if (!isNaN(item.value) && Number(item.value) > 0) {
        retval.value.push(item.value)
        retval[property].push(item.key)
        retval.value_p.push(!isNaN(item.value) && (!isNaN(totalValue) && totalValue > 0) ? (item.value / totalValue * 100).toFixed(1) : 0)
      }
    })
    return retval
  },
  portfolioOtherByValue: (state, getters, rootState, rootGetters) => (property) => {
    // get the next MAX_SECTIONS of values to report
    if (_.isNil(property) || _.isArray(property) || _.isObject(property)) return {} // invalid property type
    if (!_.isString(property)) property = property.toString()
    // create map of the passed in property and add up the value of each of the rows
    // we want the % of the total value of each sector
    let retval = { 'value': [], 'value_p': [] }
    retval[property] = []
    let totalValue = 0
    // make sure we have portfolioData
    let data = rootGetters['portfolio/portfolioData']
    if (_.isNil(data) || data.length < 1) return retval // nothing to calc
    let fprop = _.reduce(data, (obj, d) => {
      if (!_.isNil(d[property]) && !_.isNil(d.value) && !_.isNaN(d.value)) {
        // conert to title cast (start), keeping any hyphens
        let key = typeof d[property] === 'string' ? d[property].split('-') : [d[property]]
        key = _.join(_.map(key, k => { return k }), '-')
        obj[key] = (!_.isNil(obj[key])) ? obj[key] + d.value : d.value
        totalValue += d.value
      }
      return obj
    }, {})
    // make object into array of objs {key,value}
    fprop = _.flatMap(fprop, (v, k) => {
      return { key: k, value: v }
    })
    // sort by value (desc)
    fprop = _.orderBy(fprop, ['value'], ['desc'])
    // remove the first MAX_SECTIONS entries
    fprop = _.slice(fprop, MAX_SECTIONS, fprop.length)
    // go through our accumulations and assign them to the arrays
    _.each(fprop, item => {
      retval.value.push(item.value)
      retval[property].push(item.key)
      retval.value_p.push((item.value / totalValue * 100).toFixed(1))
    })
    return retval
  },
  /** portfolioROIByMonth
   * calculates the roi of the portfolio over time (organized into month segments), attempts to do some compression of data for timestamping purposes
   * @returns 
   */
  portfolioROIByMonth: (state, getters, rootState, rootGetters) => () => {
    // need to calculate the value of the portfolio over time (and to collapse 'near' data points)
    let retval = { value: [], cost: [], gain: [], value_t: [], cost_t: [], gain_t: [] }
    let data = rootGetters['portfolio/portfolioData']
    if (_.isNil(data) || _.isEmpty(data)) return retval
    let fprop = _.reduce(data, (obj, gram) => {
      if (_.isNil(gram.initial_investment) || _.isNil(gram.last_funding_round)) return obj // skip any empty/null dates
      // we want date objects for initial/last_funding
      let initial = (_.isString(gram.initial_investment)) ? moment.utc(gram.initial_investment) : gram.initial_investment
      let last = (_.isString(gram.last_funding_round)) ? moment.utc(gram.last_funding_round) : gram.last_funding_round
      // now check the month/year of the date, create that as our reference
      initial = moment(new Date(initial.year(), initial.month(), 0))
      last = moment(new Date(last.year(), last.month(), 0))
      initial = initial.toISOString()
      last = last.toISOString()
      // first do cost orering
      obj.cost[initial] = { cost: (!_.isNil(obj.cost[initial]) ? obj.cost[initial].cost + gram.cost : gram.cost) }
      // next do value ordering
      obj.value[last] = { value: (!_.isNil(obj.value[last]) ? obj.value[last].value + gram.value : gram.value) }
      // need to do gain for both initial and last_funding
      obj.gain[initial] = { gain: (!_.isNil(obj.gain[initial]) ? obj.gain[initial].gain - gram.cost : 0 - gram.cost) }
      obj.gain[last] = { gain: (!_.isNil(obj.gain[last]) ? obj.gain[last].gain + gram.value : gram.value) }
      return obj
    }, { cost: {}, value: {}, gain: {} })
    fprop.value = _.flatMap(fprop.value, (v, k) => {
      return { key: k, value: v.value, cost: 0 }
    })
    fprop.value = _.orderBy(fprop.value, ['key'], ['desc'])
    fprop.cost = _.flatMap(fprop.cost, (v, k) => {
      return { key: k, value: 0, cost: v.cost }
    })
    fprop.cost = _.orderBy(fprop.cost, ['key'], ['desc'])
    fprop.gain = _.flatMap(fprop.gain, (v, k) => {
      return { key: k, gain: v.gain }
    })
    fprop.gain = _.orderBy(fprop.gain, ['key'], ['desc'])
    // have two arrays
    // value - derived values at point in time
    // cost - derived cost at point in time
    // each has gain, derived gain (- for cost, + for value)
    // need to create three graph series, one for each: value, cost, gain
    // need to do summations of previous time stamps for all three
    _.each(fprop.value, obj => {
      retval.value = _.map(retval.value, v => { return v + obj.value })
      retval.value.push(obj.value)
      retval.value_t.push(obj.key)
    })
    _.each(fprop.cost, obj => {
      retval.cost = _.map(retval.cost, c => { return c + obj.cost })
      retval.cost.push(obj.cost)
      retval.cost_t.push(obj.key)
    })
    _.each(fprop.gain, obj => {
      retval.gain = _.map(retval.gain, g => { return g + obj.gain })
      retval.gain.push(obj.gain)
      retval.gain_t.push(obj.key)
    })
    return retval
  },
  portfolioROIByYear: (state, getters, rootState, rootGetters) => () => {
    // need to calculate the value of the portfolio over time (and to collapse 'near' data points)
    let retval = { value: [], cost: [], gain: [], value_t: [], cost_t: [], gain_t: [] }
    let data = rootGetters['portfolio/portfolioData']
    if (_.isNil(data) || _.isEmpty(data)) return retval
    let fprop = _.reduce(data, (obj, gram) => {
      if (_.isNil(gram.initial_investment) || _.isNil(gram.last_funding_round)) return obj // skip any empty/null dates
      // we want date objects for initial/last_funding
      let initial = (_.isString(gram.initial_investment)) ? moment.utc(gram.initial_investment) : gram.initial_investment
      let last = (_.isString(gram.last_funding_round)) ? moment.utc(gram.last_funding_round) : gram.last_funding_round
      // now check the month/year of the date, create that as our reference
      initial = moment(new Date(initial.year(), 1, 0))
      last = moment(new Date(last.year(), 1, 0))
      initial = initial.toISOString()
      last = last.toISOString()
      // first do cost orering
      obj.cost[initial] = { cost: (!_.isNil(obj.cost[initial]) ? obj.cost[initial].cost + gram.cost : gram.cost) }
      // next do value ordering
      obj.value[last] = { value: (!_.isNil(obj.value[last]) ? obj.value[last].value + gram.value : gram.value) }
      // need to do gain for both initial and last_funding
      obj.gain[initial] = { gain: (!_.isNil(obj.gain[initial]) ? obj.gain[initial].gain - gram.cost : 0 - gram.cost) }
      obj.gain[last] = { gain: (!_.isNil(obj.gain[last]) ? obj.gain[last].gain + gram.value : gram.value) }
      return obj
    }, { cost: {}, value: {}, gain: {} })
    fprop.value = _.flatMap(fprop.value, (v, k) => {
      return { key: k, value: v.value, cost: 0 }
    })
    fprop.value = _.orderBy(fprop.value, ['key'], ['desc'])
    fprop.cost = _.flatMap(fprop.cost, (v, k) => {
      return { key: k, value: 0, cost: v.cost }
    })
    fprop.cost = _.orderBy(fprop.cost, ['key'], ['desc'])
    fprop.gain = _.flatMap(fprop.gain, (v, k) => {
      return { key: k, gain: v.gain }
    })
    fprop.gain = _.orderBy(fprop.gain, ['key'], ['desc'])
    // have two arrays
    // value - derived values at point in time
    // cost - derived cost at point in time
    // each has gain, derived gain (- for cost, + for value)
    // need to create three graph series, one for each: value, cost, gain
    // need to do summations of previous time stamps for all three
    _.each(fprop.value, obj => {
      retval.value = _.map(retval.value, v => { return v + obj.value })
      retval.value.push(obj.value)
      retval.value_t.push(obj.key)
    })
    _.each(fprop.cost, obj => {
      retval.cost = _.map(retval.cost, c => { return c + obj.cost })
      retval.cost.push(obj.cost)
      retval.cost_t.push(obj.key)
    })
    _.each(fprop.gain, obj => {
      retval.gain = _.map(retval.gain, g => { return g + obj.gain })
      retval.gain.push(obj.gain)
      retval.gain_t.push(obj.key)
    })
    return retval
  },
  portfolioROIByProperty: (state, getters, rootState, rootGetters) => (property, convert = true) => {
    if (_.isNil(property) || _.isArray(property) || _.isObject(property)) return {} // invalid property type
    if (!_.isString(property)) property = property.toString()
    function mergeSum(objV, srcV) {
      if (_.isNil(objV)) return srcV
      if (_.isNil(srcV)) return objV
      return { value: objV.value + srcV.value, cost: objV.cost + srcV.cost, gain: objV.gain + srcV.gain }
    }
    // ROI is going to include: value, cost, and gain (value-cost)
    let retval = { 'value': [], 'cost': [], 'gain': [] }
    retval[property] = []
    let data = rootGetters['portfolio/portfolioData']
    if (_.isNil(data) || data.length < 1) return retval
    let fprop = _.reduce(data, (obj, d) => {
      if (!_.isNil(d[property]) && !_.isNil(d.value) && !_.isNil(d.cost) && !_.isNaN(d.value) && !_.isNaN(d.cost)) {
        // conert to title cast (start), keeping any hyphens
        let key = d[property]
        if (convert) {
          key = typeof d[property] === 'string' ? d[property].split('-') : [d[property]]
          key = _.join(_.map(key, k => { return k }), '-')
        }
        let subj = { value: d.value, cost: d.cost, gain: (d.value - d.cost) }
        if (!obj[key]) obj[key] = subj
        else obj = _.mergeWith({}, obj, { [key]: subj }, mergeSum)
      }
      return obj
    }, {})
    // make object into array of objs {key,value}
    fprop = _.flatMap(fprop, (v, k) => {
      return { key: k, value: v.value, cost: v.cost, gain: v.gain }
    })
    // sort by value (desc)
    fprop = _.orderBy(fprop, ['gain'], ['desc'])
    // if there are more than MAX_SECTIONS, combine the lowest into 'Other'
    // if (fprop.length > MAX_SECTIONS) {
    //   let i = 0,
    //     other = { key: 'Other', value: 0, cost: 0, gain: 0 }
    //   _.each(fprop, item => {
    //     i++;
    //     if (i > (MAX_SECTIONS - 1)) {
    //       other.value += item.value
    //       other.cost += item.cost
    //       other.gain += item.gain
    //     }
    //   })
    //   fprop = _.slice(fprop, 0, MAX_SECTIONS)
    //   fprop.push(other)
    // }

    if (property == 'stage') {
      let sortedProps = [];
      for (let i = 0; i < vals.STAGE_DEFINES.length; i++) {
        for (let j = 0; j < fprop.length; j++) {
          if (fprop[j].key.toString().toLowerCase() == vals.STAGE_DEFINES[i].toString().toLowerCase()) {
            sortedProps.push(fprop[j]);
          }
        }
      }
      fprop = sortedProps;
    }

    // go through our accumulations and assign them to the arrays
    _.each(fprop, item => {
      retval[property].push(item.key)
      retval.value.push(item.value)
      retval.cost.push(item.cost)
      retval.gain.push(item.gain)
    })
    return retval
  },
  otherCompaniesByPropertyValue: (state, getters, rootState, rootGetters) => (property, value, other = false) => {
    if (_.isNil(property) || _.isEmpty(property) || _.isNil(value) || _.isEmpty(value)) return {}
    let retval = { value: [], t_value: 0, value_p: [], cost: [], t_cost: 0, cost_p: [], gain: [], t_gain: 0, gain_p: [], names: [] }
    // get the top X companies based on the category(value) in the given property
    let data = rootGetters['portfolio/portfolioData']
    if (_.isNil(data) || _.isEmpty(data)) return retval
    let fprop = _.map(data, gram => {
      if (_.isNil(gram[property]) || gram[property] != value) return null
      retval.t_value += gram.value
      retval.t_cost += gram.cost
      retval.t_gain += (gram.value - gram.cost)
      return { value: gram.value, cost: gram.cost, gain: (gram.value - gram.cost), name: gram.name }
    })
    fprop = _.without(fprop, undefined, null)
    // order on value
    fprop = _.orderBy(fprop, ['value'], ['desc'])
    // remove the first MAX_SECTIONS (top X) companies
    if (other) fprop = _.slice(fprop, MAX_SECTIONS, fprop.length)
    // go through and return our dataset
    _.each(fprop, item => {
      retval.value.push(item.value)
      retval.value_p.push((item.value / retval.t_value * 100).toFixed(2))
      retval.cost.push(item.cost)
      retval.cost_p.push((item.cost / retval.t_cost * 100).toFixed(2))
      retval.gain.push(item.gain)
      retval.gain_p.push((item.gain / retval.t_gain * 100).toFixed(2))
      retval.names.push(item.name)
    })
    return retval
  },
  topCompaniesByPropertyValue: (state, getters, rootState, rootGetters) => (property, value) => {
    if (_.isNil(property) || _.isEmpty(property) || _.isNil(value) || _.isEmpty(value)) return {}
    let retval = { value: [], t_value: 0, value_p: [], cost: [], t_cost: 0, cost_p: [], gain: [], t_gain: 0, gain_p: [], names: [] }
    // get the top X companies based on the category(value) in the given property
    let data = rootGetters['portfolio/portfolioData']
    if (_.isNil(data) || _.isEmpty(data)) return retval
    let fprop = _.map(data, gram => {
      if (_.isNil(gram[property]) || gram[property] != value) return null
      retval.t_value += gram.value
      retval.t_cost += gram.cost
      retval.t_gain += (gram.value - gram.cost)
      return { value: gram.value, cost: gram.cost, gain: (gram.value - gram.cost), name: gram.name }
    })
    fprop = _.without(fprop, undefined, null)
    // order on value
    fprop = _.orderBy(fprop, ['value'], ['desc'])
    if (fprop.length > MAX_SECTIONS) {
      // create 'other' section
      let other = { value: 0, cost: 0, gain: 0, name: 'Other' }
        , i = 0
      _.each(fprop, item => {
        i++
        if (i > (MAX_SECTIONS - 1)) {
          other.value += item.value
          other.cost += item.cost
          other.gain += item.gain
        }
      })
      fprop = _.slice(fprop, 0, MAX_SECTIONS)
      fprop.push(other)
    }
    // go through and return our dataset
    _.each(fprop, item => {
      retval.value.push(item.value)
      retval.value_p.push((item.value / retval.t_value * 100).toFixed(2))
      retval.cost.push(item.cost)
      retval.cost_p.push((item.cost / retval.t_cost * 100).toFixed(2))
      retval.gain.push(item.gain)
      retval.gain_p.push((item.gain / retval.t_gain * 100).toFixed(2))
      retval.names.push(item.name)
    })
    return retval
  },
  diligenceData: (state, getters, rootState, rootGetters) => (property) => {
    let data = rootGetters['diligence/diligenceData'];
    data.map((item) => {
      if (item.hasOwnProperty('sector')) {
        if (item.sector.length == 0 || item.sector == '') {
          item.sector = 'Unknown';
        }
      }
      else {
        item.sector = 'Unknown';
      }
      if (item.hasOwnProperty('stage')) {
        if (item.stage.length == 0 || item.stage == '') {
          item.stage = 'Pre-Seed';
        }
      }
      else {
        item.stage = 'Pre-Seed';
      }
      if (item.hasOwnProperty('geography')) {
        if (item.geography.length == 0 || item.geography == '') {
          item.geography = 'Unspecified';
        }
      }
      else {
        item.geography = 'Unspecified';
      }
      if (item.hasOwnProperty('status')) {
        if (item.status.length == 0 || item.status == '') {
          item.status = 'Unknown';
        }
      }
      else {
        item.status = 'Unknown';
      }
    });
    let totalDataLength = data.length;
    let retval = { value: [], value_p: [], labels: [], totalDataLength: 0, data: [], groupByData: {} };
    retval.data = data;
    retval.totalDataLength = totalDataLength;
    if (totalDataLength <= 0) return retval
    let diligenceStageData = rootGetters['diligence_stage/diligenceStageData'];
    let populatedData = _.filter(data, function (obj) {
      if (obj.deal_stage) {
        let stageName = diligenceStageData.find(
          stage => stage._id == obj.deal_stage
        );
        if (stageName) obj.dealStageName = stageName.stage_name;
      }
      return obj
    });
    let groupBy = function (xs, key) {
      return xs.reduce(function (rv, x) {
        (rv[x[key]] = rv[x[key]] || []).push(x);
        return rv;
      }, {});
    };
    // let groubedByData = groupBy(populatedData, 'dealStageName');
    let groubedByData = groupBy(populatedData, property);
    retval.groupByData = groubedByData;
    retval.labels = Object.keys(groubedByData);
    _.each(retval.labels, item => {
      retval.value.push(groubedByData[item].length);
      retval.value_p.push((groubedByData[item].length / totalDataLength * 100).toFixed(2))
    })
    return retval;
  },
  dashboardPortfolioROIByProperty: (state, getters, rootState, rootGetters) => (property, subProperty = all, convert = true) => {
    if (_.isNil(property) || _.isArray(property) || _.isObject(property)) return {} // invalid property type
    if (!_.isString(property)) property = property.toString()
    function mergeSum(objV, srcV) {
      if (_.isNil(objV)) return srcV
      if (_.isNil(srcV)) return objV
      return { value: objV.value + srcV.value, cost: objV.cost + srcV.cost, gain: objV.gain + srcV.gain }
    }
    // ROI is going to include: value, cost, and gain (value-cost)
    let retval = { 'value': [], 'cost': [], 'gain': [] }
    retval[property] = []
    retval[subProperty] = []
    let data = rootGetters['portfolio/portfolioData']
    if (_.isNil(data) || data.length < 1) return retval
    _.reduce(data, (obj, d) => {
      if (!_.isNil(d[property]) && !_.isNil(d.value) && !_.isNil(d.cost) && !_.isNaN(d.value) && !_.isNaN(d.cost)) {
        // conert to title cast (start), keeping any hyphens
        let key = d[property]
        if (convert) {
          key = typeof d[property] === 'string' ? d[property].split('-') : [d[property]]
          key = _.join(_.map(key, k => { return k }), '-')
        }
        retval[property].push(key.trim());
      }
    }, {})
    retval[property] = Array.from(new Set(retval[property]));
    if (property == 'sector' || property == 'geography') {
      if (retval[property] && retval[property].length > 0) {
        retval[property] = retval[property].sort();
      }
    }
    let fprop;
    if (subProperty == 'all') {
      fprop = _.reduce(data, (obj, d) => {
        if (!_.isNil(d[property]) && !_.isNil(d.value) && !_.isNil(d.cost) && !_.isNaN(d.value) && !_.isNaN(d.cost)) {
          // conert to title cast (start), keeping any hyphens
          let key = d[property]
          if (convert) {
            key = typeof d[property] === 'string' ? d[property].split('-') : [d[property]]
            key = _.join(_.map(key, k => { return k }), '-')
          }
          let subj = { value: d.value, cost: d.cost, gain: (d.value - d.cost) }
          if (!obj[key.trim()]) obj[key.trim()] = subj
          else obj = _.mergeWith({}, obj, { [key.trim()]: subj }, mergeSum)
        }
        return obj
      }, {})
    }
    else {
      fprop = _.reduce(data, (obj, d) => {
        if (!_.isNil(d[property]) && d[property] == subProperty && !_.isNil(d.value) && !_.isNil(d.cost) && !_.isNaN(d.value) && !_.isNaN(d.cost)) {
          // conert to title cast (start), keeping any hyphens
          let key = d['name'];
          // if (convert) {
          //   key = typeof d[property] === 'string' ? d[property].split('-') : [d[property]]
          //   key = _.join(_.map(key, k => { return k }), '-')
          // }
          let subj = { value: d.value, cost: d.cost, gain: (d.value - d.cost) }
          if (!obj[key.trim()]) obj[key.trim()] = subj
          else obj = _.mergeWith({}, obj, { [key.trim()]: subj }, mergeSum)
        }
        return obj
      }, {})
    }
    // make object into array of objs {key,value}
    fprop = _.flatMap(fprop, (v, k) => {
      return { key: k, value: v.value, cost: v.cost, gain: v.gain }
    })
    // sort by value (desc)
    fprop = _.orderBy(fprop, ['gain'], ['desc'])
    // if there are more than MAX_SECTIONS, combine the lowest into 'Other'
    // if (fprop.length > MAX_SECTIONS) {
    //   let i = 0,
    //     other = { key: 'Other', value: 0, cost: 0, gain: 0 }
    //   _.each(fprop, item => {
    //     i++;
    //     if (i > (MAX_SECTIONS - 1)) {
    //       other.value += item.value
    //       other.cost += item.cost
    //       other.gain += item.gain
    //     }
    //   })
    //   fprop = _.slice(fprop, 0, MAX_SECTIONS)
    //   fprop.push(other)
    // }

    // if (property == 'stage') {
    //   let sortedProps = [];
    //   for (let i = 0; i < vals.STAGE_DEFINES.length; i++) {
    //     for (let j = 0; j < fprop.length; j++) {
    //       if (fprop[j].key.toString().toLowerCase() == vals.STAGE_DEFINES[i].toString().toLowerCase()) {
    //         sortedProps.push(fprop[j]);
    //       }
    //     }
    //   }
    //   fprop = sortedProps;
    // }

    // go through our accumulations and assign them to the arrays
    _.each(fprop, item => {
      //retval[property].push(item.key)
      retval[subProperty].push(item.key)
      retval.value.push(item.value)
      retval.cost.push(item.cost)
      retval.gain.push(item.gain)
    })
    return retval
  },
  portfolioPerformanceByYear: (state, getters, rootState, rootGetters) => () => {
    // need to calculate the value of the portfolio over time (and to collapse 'near' data points)
    let retval = { value: [], cost: [], gain: [], value_t: [], cost_t: [], gain_t: [] }
    let data = rootGetters['portfolio/portfolioData']
    if (_.isNil(data) || _.isEmpty(data)) return retval
    let fprop = _.reduce(data, (obj, gram) => {
      if (_.isNil(gram.initial_investment) || _.isNil(gram.last_funding_round)) return obj // skip any empty/null dates
      // we want date objects for initial/last_funding
      let initial = (_.isString(gram.initial_investment)) ? moment.utc(gram.initial_investment) : gram.initial_investment
      // now check the month/year of the date, create that as our reference
      initial = moment(new Date(initial.year(), 1, 0))
      initial = initial.toISOString()
      // first do cost orering
      obj.cost[initial] = { cost: (!_.isNil(obj.cost[initial]) ? obj.cost[initial].cost + gram.cost : gram.cost) }
      // next do value ordering
      obj.value[initial] = { value: (!_.isNil(obj.value[initial]) ? obj.value[initial].value + gram.value : gram.value) }
      return obj
    }, { cost: {}, value: {}, gain: {} })
    fprop.value = _.flatMap(fprop.value, (v, k) => {
      return { key: k, value: v.value, cost: 0 }
    })
    fprop.value = _.orderBy(fprop.value, ['key'], ['desc'])
    fprop.cost = _.flatMap(fprop.cost, (v, k) => {
      return { key: k, value: 0, cost: v.cost }
    })
    fprop.cost = _.orderBy(fprop.cost, ['key'], ['desc'])
    // have two arrays
    // value - derived values at point in time
    // cost - derived cost at point in time
    // each has gain, derived gain (- for cost, + for value)
    // need to create three graph series, one for each: value, cost, gain
    // need to do summations of previous time stamps for all three
    _.each(fprop.value, obj => {
      retval.value = _.map(retval.value, v => { return v + obj.value })
      retval.value.push(obj.value)
      retval.value_t.push(obj.key)
    })
    _.each(fprop.cost, obj => {
      retval.cost = _.map(retval.cost, c => { return c + obj.cost })
      retval.cost.push(obj.cost)
      retval.cost_t.push(obj.key)
    })
    return retval
  },
  portfolioPerformanceByQuarter: (state, getters, rootState, rootGetters) => () => {
    // need to calculate the value of the portfolio over time (and to collapse 'near' data points)
    let retval = { value: [], cost: [], gain: [], value_t: [], cost_t: [], gain_t: [] }
    let data = rootGetters['portfolio/portfolioData']
    if (_.isNil(data) || _.isEmpty(data)) return retval
    let fprop = _.reduce(data, (obj, gram) => {
      if (_.isNil(gram.initial_investment) || gram.initial_investment == 'Invalid date' || gram.initial_investment == '-') return obj // skip any empty/null dates
      // we want date objects for initial/last_funding
      let initial = (_.isString(gram.initial_investment)) ? moment.utc(gram.initial_investment) : gram.initial_investment
      // now check the month/year of the date, create that as our reference
      if (Number(initial.month()) + 1 <= 4) {
        initial = moment(new Date(initial.year(), 1, 0));
      }
      else if (Number(initial.month()) + 1 <= 8 && Number(initial.month()) + 1 > 4) {
        initial = moment(new Date(initial.year(), 5, 0));
      }
      else if (Number(initial.month()) + 1 <= 12 && Number(initial.month()) + 1 > 8) {
        initial = moment(new Date(initial.year(), 9, 0));
      }
      else {
        initial = moment(new Date(initial.year(), initial.month() + 1, 0));
      }
      initial = initial.toISOString()
      // first do cost orering
      obj.cost[initial] = { cost: (!_.isNil(obj.cost[initial]) ? obj.cost[initial].cost + gram.cost : gram.cost) }
      // next do value ordering
      obj.value[initial] = { value: (!_.isNil(obj.value[initial]) ? obj.value[initial].value + gram.value : gram.value) }
      return obj
    }, { cost: {}, value: {}, gain: {} })
    fprop.value = _.flatMap(fprop.value, (v, k) => {
      return { key: k, value: v.value, cost: 0 }
    })
    fprop.value = _.orderBy(fprop.value, ['key'], ['desc'])
    fprop.cost = _.flatMap(fprop.cost, (v, k) => {
      return { key: k, value: 0, cost: v.cost }
    })
    fprop.cost = _.orderBy(fprop.cost, ['key'], ['desc'])
    // have two arrays
    // value - derived values at point in time
    // cost - derived cost at point in time
    // each has gain, derived gain (- for cost, + for value)
    // need to create three graph series, one for each: value, cost, gain
    // need to do summations of previous time stamps for all three
    _.each(fprop.value, obj => {
      retval.value = _.map(retval.value, v => { return v + obj.value })
      retval.value.push(obj.value)
      retval.value_t.push(obj.key)
    })
    _.each(fprop.cost, obj => {
      retval.cost = _.map(retval.cost, c => { return c + obj.cost })
      retval.cost.push(obj.cost)
      retval.cost_t.push(obj.key)
    })
    return retval
  },
  genderChartData: (state) => {
    return state.genderData;
  },
  dashboardPortfolioROIByMonth: (state, getters, rootState, rootGetters) => () => {
    // need to calculate the value of the portfolio over time (and to collapse 'near' data points)
    let retval = { value: [], cost: [], gain: [], value_t: [], cost_t: [], gain_t: [] };
    let property = state.category;
    let subProperty = state.subCategory;
    let data = rootGetters['portfolio/portfolioData'];
    data = data.filter((item) => {
      if (property && item[property]) {
        if (subProperty) {
          if (subProperty == 'all') {
            return item;
          }
          else {
            if (item[property] == subProperty) {
              return item;
            }
          }
        }
      }
    });
    if (_.isNil(data) || _.isEmpty(data)) return retval
    let fprop = _.reduce(data, (obj, gram) => {
      if (_.isNil(gram.initial_investment) || _.isNil(gram.last_funding_round)) return obj // skip any empty/null dates
      // we want date objects for initial/last_funding
      let initial = (_.isString(gram.initial_investment)) ? moment.utc(gram.initial_investment) : gram.initial_investment
      let last = (_.isString(gram.last_funding_round)) ? moment.utc(gram.last_funding_round) : gram.last_funding_round
      // now check the month/year of the date, create that as our reference
      initial = moment(new Date(initial.year(), initial.month(), 0))
      last = moment(new Date(last.year(), last.month(), 0))
      initial = initial.toISOString()
      last = last.toISOString()
      // first do cost orering
      obj.cost[initial] = { cost: (!_.isNil(obj.cost[initial]) ? obj.cost[initial].cost + gram.cost : gram.cost) }
      // next do value ordering
      obj.value[last] = { value: (!_.isNil(obj.value[last]) ? obj.value[last].value + gram.value : gram.value) }
      // need to do gain for both initial and last_funding
      obj.gain[initial] = { gain: (!_.isNil(obj.gain[initial]) ? obj.gain[initial].gain - gram.cost : 0 - gram.cost) }
      obj.gain[last] = { gain: (!_.isNil(obj.gain[last]) ? obj.gain[last].gain + gram.value : gram.value) }
      return obj
    }, { cost: {}, value: {}, gain: {} })
    fprop.value = _.flatMap(fprop.value, (v, k) => {
      return { key: k, value: v.value, cost: 0 }
    })
    fprop.value = _.orderBy(fprop.value, ['key'], ['desc'])
    fprop.cost = _.flatMap(fprop.cost, (v, k) => {
      return { key: k, value: 0, cost: v.cost }
    })
    fprop.cost = _.orderBy(fprop.cost, ['key'], ['desc'])
    fprop.gain = _.flatMap(fprop.gain, (v, k) => {
      return { key: k, gain: v.gain }
    })
    fprop.gain = _.orderBy(fprop.gain, ['key'], ['desc'])
    // have two arrays
    // value - derived values at point in time
    // cost - derived cost at point in time
    // each has gain, derived gain (- for cost, + for value)
    // need to create three graph series, one for each: value, cost, gain
    // need to do summations of previous time stamps for all three
    _.each(fprop.value, obj => {
      retval.value = _.map(retval.value, v => { return v + obj.value })
      retval.value.push(obj.value)
      retval.value_t.push(obj.key)
    })
    _.each(fprop.cost, obj => {
      retval.cost = _.map(retval.cost, c => { return c + obj.cost })
      retval.cost.push(obj.cost)
      retval.cost_t.push(obj.key)
    })
    _.each(fprop.gain, obj => {
      retval.gain = _.map(retval.gain, g => { return g + obj.gain })
      retval.gain.push(obj.gain)
      retval.gain_t.push(obj.key)
    })
    return retval
  },
  dashboardPortfolioROIByYear: (state, getters, rootState, rootGetters) => () => {
    // need to calculate the value of the portfolio over time (and to collapse 'near' data points)
    let retval = { value: [], cost: [], gain: [], value_t: [], cost_t: [], gain_t: [] };
    let property = state.category;
    let subProperty = state.subCategory;
    let data = rootGetters['portfolio/portfolioData'];
    data = data.filter((item) => {
      if (property && item[property]) {
        if (subProperty) {
          if (subProperty == 'all') {
            return item;
          }
          else {
            if (item[property] == subProperty) {
              return item;
            }
          }
        }
      }
    });
    if (_.isNil(data) || _.isEmpty(data)) return retval
    let fprop = _.reduce(data, (obj, gram) => {
      if (_.isNil(gram.initial_investment) || _.isNil(gram.last_funding_round)) return obj // skip any empty/null dates
      // we want date objects for initial/last_funding
      let initial = (_.isString(gram.initial_investment)) ? moment.utc(gram.initial_investment) : gram.initial_investment
      let last = (_.isString(gram.last_funding_round)) ? moment.utc(gram.last_funding_round) : gram.last_funding_round
      // now check the month/year of the date, create that as our reference
      initial = moment(new Date(initial.year(), 1, 0))
      last = moment(new Date(last.year(), 1, 0))
      initial = initial.toISOString()
      last = last.toISOString()
      // first do cost orering
      obj.cost[initial] = { cost: (!_.isNil(obj.cost[initial]) ? obj.cost[initial].cost + gram.cost : gram.cost) }
      // next do value ordering
      obj.value[last] = { value: (!_.isNil(obj.value[last]) ? obj.value[last].value + gram.value : gram.value) }
      // need to do gain for both initial and last_funding
      obj.gain[initial] = { gain: (!_.isNil(obj.gain[initial]) ? obj.gain[initial].gain - gram.cost : 0 - gram.cost) }
      obj.gain[last] = { gain: (!_.isNil(obj.gain[last]) ? obj.gain[last].gain + gram.value : gram.value) }
      return obj
    }, { cost: {}, value: {}, gain: {} })
    fprop.value = _.flatMap(fprop.value, (v, k) => {
      return { key: k, value: v.value, cost: 0 }
    })
    fprop.value = _.orderBy(fprop.value, ['key'], ['desc'])
    fprop.cost = _.flatMap(fprop.cost, (v, k) => {
      return { key: k, value: 0, cost: v.cost }
    })
    fprop.cost = _.orderBy(fprop.cost, ['key'], ['desc'])
    fprop.gain = _.flatMap(fprop.gain, (v, k) => {
      return { key: k, gain: v.gain }
    })
    fprop.gain = _.orderBy(fprop.gain, ['key'], ['desc'])
    // have two arrays
    // value - derived values at point in time
    // cost - derived cost at point in time
    // each has gain, derived gain (- for cost, + for value)
    // need to create three graph series, one for each: value, cost, gain
    // need to do summations of previous time stamps for all three
    _.each(fprop.value, obj => {
      retval.value = _.map(retval.value, v => { return v + obj.value })
      retval.value.push(obj.value)
      retval.value_t.push(obj.key)
    })
    _.each(fprop.cost, obj => {
      retval.cost = _.map(retval.cost, c => { return c + obj.cost })
      retval.cost.push(obj.cost)
      retval.cost_t.push(obj.key)
    })
    _.each(fprop.gain, obj => {
      retval.gain = _.map(retval.gain, g => { return g + obj.gain })
      retval.gain.push(obj.gain)
      retval.gain_t.push(obj.key)
    })
    return retval
  },
  category: (state) => {
    return state.category;
  },
  subCategory: (state) => {
    return state.subCategory;
  },
  dashboardPortfolioValueByProperty: (state, getters, rootState, rootGetters) => (property, mainProperty) => {
    if (_.isNil(property) || _.isArray(property) || _.isObject(property) || _.isNil(mainProperty) || _.isArray(mainProperty) || _.isObject(mainProperty)) return {} // invalid property type
    if (!_.isString(property)) property = property.toString();
    if (!_.isString(mainProperty)) mainProperty = mainProperty.toString()
    // create map of the passed in property and add up the value of each of the rows
    // we want the % of the total value of each sector
    let retval = { 'value': [], 'value_p': [] }
    retval[property] = []
    let totalValue = 0
    // make sure we have portfolioData
    let data = rootGetters['portfolio/portfolioData']
    if (_.isNil(data) || data.length < 1) return retval // nothing to calc
    let fprop;
    if (mainProperty == 'Portfolio Value') {
      fprop = _.reduce(data, (obj, d) => {
        if (!_.isNil(d[property]) && !_.isNil(d.value) && !_.isNaN(d.value)) {
          // conert to title cast (start), keeping any hyphens
          let key = typeof d[property] === 'string' ? d[property].split('-') : [d[property]]
          key = _.join(_.map(key, k => { return k }), '-')
          obj[key] = (!_.isNil(obj[key])) ? obj[key] + d.value : d.value
          //totalValue += d.value
          totalValue = !isNaN(d.value) ? totalValue + Number(d.value) : totalValue + 0;
        }
        return obj
      }, {});
    }
    else {
      fprop = _.reduce(data, (obj, d) => {
        if (!_.isNil(d[property]) && !_.isNil(d.value) && !_.isNaN(d.value)) {
          // conert to title cast (start), keeping any hyphens
          let key = typeof d[property] === 'string' ? d[property].split('-') : [d[property]]
          key = _.join(_.map(key, k => { return k }), '-')
          obj[key] = (!_.isNil(obj[key])) ? obj[key] + 1 : 1;
          //totalValue += d.value
          totalValue++;
        }
        return obj
      }, {});
    }
    // make object into array of objs {key,value}
    fprop = _.flatMap(fprop, (v, k) => {
      return { key: k, value: v }
    })
    // sort by value (desc)
    fprop = _.orderBy(fprop, ['value'], ['desc'])
    // if there are more than MAX_SECTIONS, combine the lowest into 'Other'
    if (fprop.length > MAX_SECTIONS) {
      let i = 0,
        other = { key: 'Other', value: 0 }
      _.each(fprop, item => {
        i++;
        if (i > (MAX_SECTIONS - 1)) {
          //other.value += item.value
          if (mainProperty == 'Portfolio Value') {
            other.value = !isNaN(item.value) ? other.value + Number(item.value) : other.value + 0;
          }
          else {
            other.value += 1;
          }
        }
      })
      fprop = _.slice(fprop, 0, MAX_SECTIONS)
      fprop.push(other)
    }
    // go through our accumulations and assign them to the arrays
    _.each(fprop, item => {
      retval.value.push(item.value)
      retval[property].push(item.key)
      retval.value_p.push(!isNaN(item.value) && (!isNaN(totalValue) && totalValue > 0) ? (item.value / totalValue * 100).toFixed(1) : 0);
    })
    return retval
  },
  dashboardTopCompaniesByPropertyValue: (state, getters, rootState, rootGetters) => (property, value, mainProperty) => {
    if (_.isNil(property) || _.isEmpty(property) || _.isNil(value) || _.isEmpty(value) || _.isNil(mainProperty) || _.isEmpty(mainProperty)) return {}
    let retval = { value: [], t_value: 0, value_p: [], cost: [], t_cost: 0, cost_p: [], gain: [], t_gain: 0, gain_p: [], names: [] }
    // get the top X companies based on the category(value) in the given property
    let data = rootGetters['portfolio/portfolioData']
    if (_.isNil(data) || _.isEmpty(data)) return retval
    let fprop;
    if (mainProperty == 'Portfolio Value') {
      fprop = _.map(data, gram => {
        if (_.isNil(gram[property]) || gram[property] != value) return null
        retval.t_value += gram.value
        return { value: gram.value, name: gram.name }
      });
    }
    else {
      fprop = _.map(data, gram => {
        if (_.isNil(gram[property]) || gram[property] != value) return null
        retval.t_value++;
        return { value: 1, name: gram.name }
      });
    }
    fprop = _.without(fprop, undefined, null)
    // order on value
    fprop = _.orderBy(fprop, ['value'], ['desc'])
    if (fprop.length > MAX_SECTIONS) {
      // create 'other' section
      let other = { value: 0, name: 'Other' }
        , i = 0
      _.each(fprop, item => {
        i++
        if (i > (MAX_SECTIONS - 1)) {
          if (mainProperty == 'Portfolio Value') {
            other.value += item.value
          }
          else {
            other.value += 1;
          }
        }
      })
      fprop = _.slice(fprop, 0, MAX_SECTIONS)
      fprop.push(other)
    }
    // go through and return our dataset
    _.each(fprop, item => {
      retval.value.push(item.value)
      retval.value_p.push((item.value / retval.t_value * 100).toFixed(2))
      retval.names.push(item.name)
    })
    return retval
  },
  dashboardPortfolioOtherByValue: (state, getters, rootState, rootGetters) => (property, mainProperty) => {
    // get the next MAX_SECTIONS of values to report
    if (_.isNil(property) || _.isArray(property) || _.isObject(property) || _.isNil(mainProperty) || _.isArray(mainProperty) || _.isObject(mainProperty)) return {} // invalid property type
    if (!_.isString(property)) property = property.toString();
    if (!_.isString(mainProperty)) mainProperty = mainProperty.toString()
    // create map of the passed in property and add up the value of each of the rows
    // we want the % of the total value of each sector
    let retval = { 'value': [], 'value_p': [] }
    retval[property] = []
    let totalValue = 0
    // make sure we have portfolioData
    let data = rootGetters['portfolio/portfolioData']
    if (_.isNil(data) || data.length < 1) return retval // nothing to calc
    let fprop;
    if (mainProperty == 'Portfolio Value') {
      fprop = _.reduce(data, (obj, d) => {
        if (!_.isNil(d[property]) && !_.isNil(d.value) && !_.isNaN(d.value)) {
          // conert to title cast (start), keeping any hyphens
          let key = typeof d[property] === 'string' ? d[property].split('-') : [d[property]]
          key = _.join(_.map(key, k => { return k }), '-')
          obj[key] = (!_.isNil(obj[key])) ? obj[key] + d.value : d.value
          totalValue += d.value
        }
        return obj
      }, {});
    }
    else {
      fprop = _.reduce(data, (obj, d) => {
        if (!_.isNil(d[property]) && !_.isNil(d.value) && !_.isNaN(d.value)) {
          // conert to title cast (start), keeping any hyphens
          let key = typeof d[property] === 'string' ? d[property].split('-') : [d[property]]
          key = _.join(_.map(key, k => { return k }), '-')
          obj[key] = (!_.isNil(obj[key])) ? obj[key] + 1 : 1;
          totalValue++;
        }
        return obj
      }, {});
    }
    // make object into array of objs {key,value}
    fprop = _.flatMap(fprop, (v, k) => {
      return { key: k, value: v }
    })
    // sort by value (desc)
    fprop = _.orderBy(fprop, ['value'], ['desc'])
    // remove the first MAX_SECTIONS entries
    fprop = _.slice(fprop, MAX_SECTIONS, fprop.length)
    // go through our accumulations and assign them to the arrays
    _.each(fprop, item => {
      retval.value.push(item.value)
      retval[property].push(item.key)
      retval.value_p.push((item.value / totalValue * 100).toFixed(1))
    })
    return retval
  },
  dashboardOtherCompaniesByPropertyValue: (state, getters, rootState, rootGetters) => (property, value, mainProperty, other = false) => {
    if (_.isNil(property) || _.isEmpty(property) || _.isNil(value) || _.isEmpty(value) || _.isNil(mainProperty) || _.isEmpty(mainProperty)) return {}
    let retval = { value: [], t_value: 0, value_p: [], cost: [], t_cost: 0, cost_p: [], gain: [], t_gain: 0, gain_p: [], names: [] }
    // get the top X companies based on the category(value) in the given property
    let data = rootGetters['portfolio/portfolioData']
    if (_.isNil(data) || _.isEmpty(data)) return retval
    let fprop;
    if (mainProperty == 'Portfolio Value') {
      fprop = _.map(data, gram => {
        if (_.isNil(gram[property]) || gram[property] != value) return null
        retval.t_value += gram.value
        return { value: gram.value, name: gram.name }
      });
    }
    else {
      fprop = _.map(data, gram => {
        if (_.isNil(gram[property]) || gram[property] != value) return null
        retval.t_value++;
        return { value: 1, name: gram.name }
      });
    }
    fprop = _.without(fprop, undefined, null)
    // order on value
    fprop = _.orderBy(fprop, ['value'], ['desc'])
    // remove the first MAX_SECTIONS (top X) companies
    if (other) fprop = _.slice(fprop, MAX_SECTIONS, fprop.length)
    // go through and return our dataset
    _.each(fprop, item => {
      retval.value.push(item.value)
      retval.value_p.push((item.value / retval.t_value * 100).toFixed(2))
      retval.names.push(item.name)
    })
    return retval
  },
  fromDate: (state) => {
    return state.fromDate;
  },
  toDate: (state) => {
    return state.toDate;
  },
  funnelChartData: (state, getters, rootState, rootGetters) => {
    let allCompanies = [], investedCompanies = [], diligenceCompanies = [], allDiligenceCompanies = [], passedCompanies = [], retval = [];
    allCompanies = rootGetters["portfolio/portfolioAll"];
    if (state.fromDate != '' && state.toDate != '') {
      let startDate = new Date(state.fromDate);
      let endDate = new Date(state.toDate);
      allCompanies = allCompanies.filter((item) => {
        let date = item.hasOwnProperty('created') && new Date(item.created) ? new Date(item.created) : new Date();
        date = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        return (date >= startDate && date <= endDate);
      });
    }
    allCompanies.map((item) => {
      if (item && item.hasOwnProperty('user_investment_status')) {
        if (item.user_investment_status == 'Invested') {
          investedCompanies.push(item);
        }
        else {
          if (item.user_investment_status == 'Diligence') {
            allDiligenceCompanies.push(item);
          }
        }
      }
    });
    let portfolioAll = rootGetters["portfolio/portfolioAll"];
    if (portfolioAll.length > 0) {
      if (state.fromDate != '' && state.toDate != '') {
        let startDate = new Date(state.fromDate);
        let endDate = new Date(state.toDate);
        investedCompanies = portfolioAll.filter((item) => {
          if (item && item.hasOwnProperty('user_investment_status') && item.user_investment_status == 'Invested') {
            let date = item && item.hasOwnProperty('initial_investment') && new Date(item.initial_investment) ?
              new Date(item.initial_investment)
              : item.hasOwnProperty('created') && new Date(item.created)
                ? new Date(item.created)
                : new Date();
            date = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
            return (date >= startDate && date <= endDate);
          }
        });
      }
    }
    let activeDiligenceCompanies = rootGetters['diligence/activeDiligenceData'];
    let archivedDiligenceCompanies = rootGetters['diligence/archivedDiligenceData'];
    if (allDiligenceCompanies.length > 0) {
      allDiligenceCompanies.map((item) => {
        if (item && item.hasOwnProperty('_id')) {
          let activeDiligenceDataIndex = activeDiligenceCompanies.findIndex((activeDiligenceItem) => {
            if (activeDiligenceItem && activeDiligenceItem.hasOwnProperty('portfolio_item') && activeDiligenceItem.portfolio_item == item._id) {
              return item;
            }
          });
          if (activeDiligenceDataIndex >= 0) {
            diligenceCompanies.push(activeDiligenceCompanies[activeDiligenceDataIndex]);
          }
          let archivedDiligenceDataIndex = archivedDiligenceCompanies.findIndex((archivedDiligenceItem) => {
            if (archivedDiligenceItem && archivedDiligenceItem.hasOwnProperty('portfolio_item') && archivedDiligenceItem.portfolio_item == item._id) {
              return item;
            }
          });
          if (archivedDiligenceDataIndex >= 0) {
            passedCompanies.push(archivedDiligenceCompanies[archivedDiligenceDataIndex]);
          }
        }
      });
    }
    allCompanies = [...diligenceCompanies, ...passedCompanies, ...investedCompanies];
    retval[0] = {
      label: allCompanies.length && allCompanies.length > 0
        ?
        allCompanies.length == 1 ?
          `${allCompanies.length} company was added to this workspace`
          : `${allCompanies.length} companies were added to this workspace`
        : `${allCompanies.length} companies were added to this workspace`,
      value: allCompanies.length,
      //color: "#78DE87",
      color: "#3D70ED",
      displayvalue: allCompanies.length && allCompanies.length > 0 ?
        allCompanies.length == 1 ?
          `${allCompanies.length} company has been added to this workspace`
          : `${allCompanies.length} companies have been added to this workspace`
        : `${allCompanies.length} companies have been added to this workspace`,
    };
    retval[1] = {
      label: diligenceCompanies.length && diligenceCompanies.length > 0 ?
        diligenceCompanies.length == 1 ?
          `${diligenceCompanies.length} company was added to diligence`
          : `${diligenceCompanies.length} companies were added to diligence`
        : `${diligenceCompanies.length} companies were added to diligence`,
      value: diligenceCompanies.length,
      //color: "#D0F2D5",
      color: "#2968AA",
      displayvalue: allCompanies.length > 0 ?
        `${(Number(diligenceCompanies.length / allCompanies.length) * 100).toFixed()}% of the companies converted to the diligence stage`
        : "0% of the companies converted to the diligence stage",
    };
    retval[2] = {
      label: passedCompanies.length && passedCompanies.length > 0 ?
        passedCompanies.length == 1 ?
          `${passedCompanies.length} company was not a fit`
          : `${passedCompanies.length} companies were not a fit`
        : `${passedCompanies.length} companies were not a fit`,
      value: passedCompanies.length,
      //color: "#FFD562",
      color: '#539E55',
      displayvalue: allCompanies.length > 0 ?
        `${(Number(passedCompanies.length / allCompanies.length) * 100).toFixed()}% of the companies were not a fit`
        : "0% of the companies were not a fit",
    };
    retval[3] = {
      label: investedCompanies.length && investedCompanies.length > 0 ?
        investedCompanies.length == 1 ?
          `You invested in ${investedCompanies.length} company`
          : `You invested in ${investedCompanies.length} companies`
        : `You invested in ${investedCompanies.length} companies`,
      value: investedCompanies.length,
      //color: "#FFECB9",
      color: '#7CD500',
      displayvalue: allCompanies.length > 0 ?
        `${(Number(investedCompanies.length / allCompanies.length) * 100).toFixed()}% of the companies were invested in`
        : "0% of the companies were invested in",
    };
    return retval;
  },
  bubbleFromDate: (state) => {
    return state.bubbleFromDate;
  },
  bubbleToDate: (state) => {
    return state.bubbleToDate;
  },
  dashboardBubbleByProperty: (state, getters, rootState, rootGetters) => {
    let retval = {}, portfolioData = [], datasets = [], labels = [];
    portfolioData = rootGetters["portfolio/portfolioData"];
    if (state.bubbleToDate != '' && state.bubbleFromDate != '') {
      if (portfolioData.length > 0) {
        let startDate = new Date(state.bubbleFromDate);
        let endDate = new Date(state.bubbleToDate);
        portfolioData = portfolioData.filter((item) => {
          let date = item && item.hasOwnProperty('initial_investment') && new Date(item.initial_investment) ?
            new Date(item.initial_investment)
            : item.hasOwnProperty('created') && new Date(item.created)
              ? new Date(item.created)
              : new Date();
          date = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
          return (date >= startDate && date <= endDate);
        });
      }
    }
    portfolioData.map((item) => {
      if (item) {
        let obj = {};
        let data = [];
        let multiple = 0;
        multiple = item.hasOwnProperty('cost') && item.hasOwnProperty('value') && item.cost &&
          item.value &&
          Number(item.cost) > 0 &&
          Number(item.value) > 0
          ? (Number(item.value) / Number(item.cost)).toFixed(2)
          : 0;
        obj.label = item.hasOwnProperty('name') ? item.name : '';
        //labels.push(obj.label);
        obj.backgroundColor = multiple && multiple > 1 ? '#73D600' : multiple == 1 ? '#006CFF' : '#fff'
        if (multiple < 1) {
          obj.borderColor = '#24A8FD';
          obj.borderWidth = 2;
        }
        data[0] = {
          x: item && item.hasOwnProperty('initial_investment') && item.initial_investment && new Date(item.initial_investment) && new Date(item.initial_investment) != 'Invalid Date' ?
            new Date(item.initial_investment).toISOString()
            : item.hasOwnProperty('created') && new Date(item.created)
              ? new Date(item.created).toISOString()
              : new Date().toISOString(),
          y: Number(multiple),
          r: 5
        };
        labels.push(moment.utc(data[0].x).format('MM-DD-YYYY'));
        obj.data = data;
        datasets.push(obj);
      }
    });
    retval.datasets = [];
    retval.datasets = datasets;
    retval.labels = [];
    retval.labels = labels;
    return retval;
  },
  dashboardBubbleChartByProperty: (state, getters, rootState, rootGetters) => (property, subProperty = all, convert = true) => {
    if (_.isNil(property) || _.isArray(property) || _.isObject(property)) return {} // invalid property type
    if (!_.isString(property)) property = property.toString()
    function mergeSum(objV, srcV) {
      if (_.isNil(objV)) return srcV
      if (_.isNil(srcV)) return objV
      return { multiple: objV.multiple + srcV.multiple, total: objV.total + srcV.total }
    }
    let retval = { 'multiple': [] }, portfolioData = [];
    portfolioData = rootGetters["portfolio/portfolioData"];
    if (state.bubbleToDate != '' && state.bubbleFromDate != '') {
      if (portfolioData.length > 0) {
        let startDate = new Date(state.bubbleFromDate);
        let endDate = new Date(state.bubbleToDate);
        portfolioData = portfolioData.filter((item) => {
          let date = item && item.hasOwnProperty('initial_investment') && new Date(item.initial_investment) ?
            new Date(item.initial_investment)
            : item.hasOwnProperty('created') && new Date(item.created)
              ? new Date(item.created)
              : new Date();
          date = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
          return (date >= startDate && date <= endDate);
        });
      }
    }
    retval[property] = [];
    retval[subProperty] = [];
    let data = portfolioData;
    _.reduce(data, (obj, d) => {
      if (!_.isNil(d[property])) {
        // conert to title cast (start), keeping any hyphens
        let key = d[property]
        if (convert) {
          key = typeof d[property] === 'string' ? d[property].split('-') : [d[property]]
          key = _.join(_.map(key, k => { return k }), '-')
        }
        retval[property].push(key.trim());
      }
    }, {})
    retval[property] = Array.from(new Set(retval[property]));
    if (property == 'sector' || property == 'geography' || property == 'name') {
      if (retval[property] && retval[property].length > 0) {
        retval[property] = retval[property].sort();
      }
    }
    if (property == 'stage') {
      let sortedProps = [];
      for (let i = 0; i < vals.BUBBLE_STAGE_DEFINES.length; i++) {
        for (let j = 0; j < retval[property].length; j++) {
          if (retval[property][j].toString().toLowerCase() == vals.BUBBLE_STAGE_DEFINES[i].toString().toLowerCase()) {
            sortedProps.push(retval[property][j]);
          }
        }
      }
      retval[property] = sortedProps;
    }
    let fprop;
    if (subProperty == 'all') {
      fprop = _.reduce(data, (obj, d) => {
        if (!_.isNil(d[property])) {
          // conert to title cast (start), keeping any hyphens
          let key = d[property]
          if (convert) {
            key = typeof d[property] === 'string' ? d[property].split('-') : [d[property]]
            key = _.join(_.map(key, k => { return k }), '-')
          }
          let subj = {
            multiple: d.hasOwnProperty('cost') && d.hasOwnProperty('value') && Number(d.cost) > 0 && Number(d.value) > 0 ? Number((Number(d.value) / Number(d.cost)).toFixed(2))
              : 0,
            total: 1
          }
          if (!obj[key.trim()]) obj[key.trim()] = subj
          else obj = _.mergeWith({}, obj, { [key.trim()]: subj }, mergeSum)
        }
        return obj
      }, {});
    }
    else {
      fprop = _.reduce(data, (obj, d) => {
        if (!_.isNil(d[property]) && d[property] == subProperty) {
          // conert to title cast (start), keeping any hyphens
          let key = d['name'];
          // if (convert) {
          //   key = typeof d[property] === 'string' ? d[property].split('-') : [d[property]]
          //   key = _.join(_.map(key, k => { return k }), '-')
          // }
          let subj = {
            multiple: d.hasOwnProperty('cost') && d.hasOwnProperty('value') && Number(d.cost) > 0 && Number(d.value) > 0 ? Number((Number(d.value) / Number(d.cost)).toFixed(2))
              : 0,
            total: 1
          };
          if (!obj[key.trim()]) obj[key.trim()] = subj;
          else obj = _.mergeWith({}, obj, { [key.trim()]: subj }, mergeSum);
        }
        return obj;
      }, {});
    }
    // make object into array of objs {key,value}
    fprop = _.flatMap(fprop, (v, k) => {
      return { key: k, multiple: v.multiple, total: v.total }
    });
    //fprop = _.orderBy(fprop, ['multiple'], ['desc']);
    // arranging in alphabetic order
    fprop = _.orderBy(fprop, ['key'], ['asc']);
    _.each(fprop, item => {
      //retval[property].push(item.key)
      retval[subProperty].push(item.key);
      let multiple = item.hasOwnProperty('total') && item.total && item.hasOwnProperty('multiple') && item.multiple
        ? Number((Number(item.multiple) / Number(item.total)).toFixed(2)) : 0;
      retval.multiple.push(multiple);
    });
    return retval;
  },
  tractionData: (state) => {
    return state.tractionData;
  },
  mrrKpiGraphData: (state, getters, rootState, rootGetters) => (ucid) => {
    let retval = { mrr: [], mrr_t: [], arr: [], arr_t: [], other: [], other_t: [], assumption: [], assumption_t: [] };
    if (ucid < 0) {
      return retval;
    }
    let tractionData = JSON.parse(JSON.stringify(state.tractionData));
    let portfolioTractionData = [];
    let currentPortfolioData = rootGetters['temp/currentPortfolioItems'](ucid);
    if (!currentPortfolioData.hasOwnProperty('_id')) {
      return retval;
    }
    portfolioTractionData = tractionData.filter((item) => {
      if (item) {
        if (item.hasOwnProperty('portfolio_item') && item.portfolio_item == currentPortfolioData._id) {
          return item;
        }
      }
    });
    if (portfolioTractionData.length == 0) {
      return retval;
    }
    portfolioTractionData.map((item, index) => {
      retval.mrr.push(item.mrr ? Number(item.mrr) : 0);
      retval.mrr_t.push(item.created ? new Date(item.created).toISOString() : new Date().toISOString());
      retval.arr.push(item.arr ? Number(item.arr) : 0);
      retval.arr_t.push(item.created ? new Date(item.created).toISOString() : new Date().toISOString());
      retval.other.push(item.revenue ? Number(item.revenue) : 0);
      retval.other_t.push(item.created ? new Date(item.created).toISOString() : new Date().toISOString());
      let cash = item.cash ? Number(item.cash) : 0;
      let monthlyBurn = item.monthlyBurn ? Number(item.monthlyBurn) : 0;
      let mrr = item.mrr ? Number(item.mrr) : 0;
      // let difference = mrr > monthlyBurn ? Number((mrr - monthlyBurn).toFixed()) : Number((mrr + monthlyBurn).toFixed());
      let difference = Math.abs(Number((mrr - monthlyBurn).toFixed()));
      let assumption = difference && difference > 0 && cash && cash > 0 ? Number((Number(cash) / Number(difference)).toFixed()) : 0;
      let created = item.created ? new Date(item.created).toISOString() : new Date().toISOString();
      retval.assumption.push(difference);
      retval.assumption_t.push(created);
      if (index == 0) {
        retval.assumption.push(difference);
        retval.assumption_t.push(new Date(
          new Date(created).setMonth(
            new Date(created).getMonth() +
            Math.round(assumption)
          )
        ).toISOString());
      }
    });
    return retval;
  },
  tractionLastUpdatedDate: (state) => {
    return state.tractionLastUpdatedDate ? moment.utc(state.tractionLastUpdatedDate).format('MM-DD-YYYY') : '';
  },
  portfolioTractionData: (state) => {
    return state.portfolioTractionData;
  },
  churnKpiGraphData: (state, getters, rootState, rootGetters) => (ucid) => {
    let retval = { customers: [], customers_t: [], payingCustomers: [], payingCustomers_t: [], churn: [], churn_t: [], churnPercentage: [] };
    if (ucid < 0) {
      return retval;
    }
    let tractionData = JSON.parse(JSON.stringify(state.tractionData));
    let portfolioTractionData = [];
    let currentPortfolioData = rootGetters['temp/currentPortfolioItems'](ucid);
    if (!currentPortfolioData.hasOwnProperty('_id')) {
      return retval;
    }
    portfolioTractionData = tractionData.filter((item) => {
      if (item) {
        if (item.hasOwnProperty('portfolio_item') && item.portfolio_item == currentPortfolioData._id) {
          return item;
        }
      }
    });
    if (portfolioTractionData.length == 0) {
      return retval;
    }
    portfolioTractionData.map((item) => {
      retval.customers.push(item.mrr ? Number(item.customers) : 0);
      retval.customers_t.push(item.created ? new Date(item.created).toISOString() : new Date().toISOString());
      retval.payingCustomers.push(item.payingCustomers ? Number(item.payingCustomers) : 0);
      retval.payingCustomers_t.push(item.created ? new Date(item.created).toISOString() : new Date().toISOString());
      retval.churn.push(item.churn ? Number(item.churn) : 0);
      retval.churn_t.push(item.created ? new Date(item.created).toISOString() : new Date().toISOString());
      retval.churnPercentage.push(item.churnPercentage ? Number(item.churnPercentage) : 0);
    });
    return retval;
  },
  companyTractionData: (state) => (portfolioItem) => {
    let tractionData = JSON.parse(JSON.stringify(state.tractionData));
    let currentCompanyTractionData = [];
    currentCompanyTractionData = tractionData.filter((item) => {
      if (item) {
        if (item.hasOwnProperty('portfolio_item') && item.portfolio_item == portfolioItem) {
          return item;
        }
      }
    });
    if (currentCompanyTractionData.length > 0) {
      currentCompanyTractionData.sort((a, b) => {
        return new Date(b.created) - new Date(a.created);
      })
    }
    return currentCompanyTractionData;
  },
  statusChartData: (state, getters, rootState, rootGetters) => {
    let portfolioData = rootGetters["portfolio/portfolioData"];
    let retval = { active: [], ob: [], exit: [], totalDataLength: 0 };
    if (portfolioData.length == 0) {
      return retval;
    }
    portfolioData.map((item) => {
      if (item) {
        if (item.hasOwnProperty('status')) {
          if (item.status == 'Active') {
            retval.active.push(item);
          }
          if (item.status == 'Out of Business' || item.status == 'Closed') {
            retval.ob.push(item);
          }
          if (item.status == 'Exit' || item.status == 'Acquired' || item.status == 'IPO') {
            retval.exit.push(item);
          }
        }
      }
    });
    retval.totalDataLength = retval.active.length + retval.ob.length + retval.exit.length;
    return retval;
  },
  commitmentChartData: (state, getters, rootState, rootGetters) => {
    let detailsData = rootGetters["lpAllocations/lpDetailsData"];
    let retval = { called: 0, called_p: 0, uncalled: 0, uncalled_p: 0, totalCommitment: 0, totalCalledUncalled: 0 };
    if (detailsData.length == 0) {
      return retval;
    }
    retval.totalCommitment = detailsData[0].hasOwnProperty('capital_commitment')
      && Number(detailsData[0].capital_commitment) > 0
      ? Number(detailsData[0].capital_commitment) : 0;
    retval.called = detailsData[0].hasOwnProperty('capital_called')
      && Number(detailsData[0].capital_called) > 0
      ? Number(detailsData[0].capital_called) : 0;
    retval.uncalled = detailsData[0].hasOwnProperty('remaining_commitment')
      && Number(detailsData[0].remaining_commitment) > 0
      ? Number(detailsData[0].remaining_commitment) : 0;
    retval.totalCalledUncalled = retval.called + retval.uncalled;
    retval.called_p = Number(retval.totalCalledUncalled) > 0 && Number(retval.called) > 0
      ? Number((Number(retval.called) / Number(retval.totalCalledUncalled)).toFixed(2) * 100) : 0;
    retval.uncalled_p = Number(retval.totalCalledUncalled) > 0 && Number(retval.uncalled) > 0
      ? Number((Number(retval.uncalled) / Number(retval.totalCalledUncalled)).toFixed(2) * 100) : 0;
    return retval;
  },
  fundKpiData: (state) => {
    return state.fundKpiData;
  },
  dpiChartData: (state) => {
    let dpiData = JSON.parse(JSON.stringify(state.fundKpiData));
    let retval = { x: [], y: [] };
    if (dpiData.length == 0) {
      return retval;
    }
    dpiData = dpiData.sort((a, b) => {
      return new Date(a.created) - new Date(b.created);
    });
    dpiData.map((item, index) => {
      if (item) {
        retval.x.push(index + 1);
        if (item.hasOwnProperty('fund_dpi') && item.fund_dpi > 0) {
          retval.y.push(Number(Number(item.fund_dpi).toFixed(2)));
        }
        else {
          retval.y.push(0);
        }
      }
    });
    return retval;
  },
  callsDistributonsChartData: (state, getters, rootState, rootGetters) => {
    let retval = { calls: [], calls_t: [], distributions: [], distributions_t: [] };
    let callsData = rootGetters["lpAllocations/capitalCallData"];
    let distributionData = rootGetters["lpAllocations/distributionData"];
    if (distributionData && distributionData.length == 0 && callsData && callsData.length == 0) {
      return retval;
    }
    callsData.map((item) => {
      if (item) {
        retval.calls.push(item.capital_called_amount ? Number(item.capital_called_amount) : 0);
        retval.calls_t.push(item.last_call ?
          new Date(item.last_call).toISOString()
          : item.created ?
            new Date(item.created).toISOString()
            : new Date().toISOString());
      }
    });
    distributionData.map((item) => {
      if (item) {
        retval.distributions.push(item.distributions ? Number(item.distributions) : 0);
        retval.distributions_t.push(item.last_distribution ?
          new Date(item.last_distribution).toISOString()
          : item.created ?
            new Date(item.created).toISOString()
            : new Date().toISOString());
      }
    });
    return retval;
  }
}

const mutations = {
  initialize(state) {
    state.genderData = {};
    state.category = 'sector';
    state.subCategory = 'all';
    state.fromDate = '';
    state.toDate = '';
    state.bubbleFromDate = '';
    state.bubbleToDate = '';
    state.tractionData = [];
    state.tractionLastUpdatedDate = '';
    state.portfolioTractionData = [];
    state.fundKpiData = [];
  },
  setCategory(state, payload) {
    state.category = payload;
  },
  setSubCategory(state, payload) {
    state.subCategory = payload;
  },
  setInitialValues(state) {
    state.category = 'sector';
    state.subCategory = 'all';
  },
  setFromDate(state, payload) {
    state.fromDate = payload;
  },
  setToDate(state, payload) {
    state.toDate = payload;
  },
  setBubbleFromDate(state, payload) {
    state.bubbleFromDate = payload;
  },
  setBubbleToDate(state, payload) {
    state.bubbleToDate = payload;
  },
  setTractionLastUpdatedDate(state, payload) {
    state.tractionLastUpdatedDate = payload;
  },
  setPortfolioTractionData(state, payload) {
    state.portfolioTractionData = payload;
  },
  setFundKpiData(state, payload) {
    state.fundKpiData = payload;
  }
}

const actions = {
  start({ state, dispatch, commit }) {
    commit('initialize')
    dispatch('getGenderChartData')
      .then(() => {
      })
      .catch(() => { })
    dispatch('getTractionData')
      .then(() => {
      })
      .catch(() => { })
  },
  getGenderChartData({ state, rootGetters }, payload) {
    let fiifi = JSON.parse(localStorage.fiifi).user
    if (fiifi && fiifi.workspace_id && fiifi.workspace_id.length > 0) {
      return api.get(`/genderGraph/${fiifi.workspace_id}`, {})
        .then((response) => {
          state.genderData = response.data.encrypt.success[0];
          return Promise.resolve(response.data.encrypt.success[0]);
        })
        .catch((error) => {
          console.log('error from genderGraph api', error);
          return Promise.reject(error);
        })
    }

  },
  getTractionData({ state, rootGetters }, payload) {
    let fiifi = JSON.parse(localStorage.fiifi).user;
    if (fiifi && fiifi.workspace_id && fiifi.workspace_id.length > 0) {
      return api.get(`/${fiifi.workspace_id}/traction`, {})
        .then((response) => {
          state.tractionData = response.data.encrypt.success[0];
          return Promise.resolve(response.data.encrypt.success[0]);
        })
        .catch((error) => {
          console.log('error from traction total data api', error);
          return Promise.reject(error);
        })
    }
  },
  getFundKpiData({ state, commit }, payload) {
    if (payload && payload.length > 0) {
      return api
        .get(`${payload}/fundKpi`, {})
        .then((data) => {
          commit('setFundKpiData', data.data.encrypt.success);
          return Promise.resolve(data.data.encrypt.success);
        })
        .catch((error) => {
          console.log("error in getFundKpiData store action", error);
          return Promise.reject(error);
        });
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}